import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ResponsibleUse from "@/pages/ResponsibleUse";
import AppLayout from "@/app/AppLayout";
import Dashboard from "@/app/Dashboard";
import Chat from "@/app/Chat";
import Projects from "@/app/Projects";
import ScienceSearch from "@/app/ScienceSearch";
import Documents from "@/app/Documents";
import Bibliography from "@/app/Bibliography";
import Similarity from "@/app/Similarity";
import Notes from "@/app/Notes";
import Messages from "@/app/Messages";
import Billing from "@/app/Billing";
import Settings from "@/app/Settings";
import AdminApp from "@/admin/AdminApp";
import PublicForm from "@/pages/PublicForm";
import Forms from "@/app/Forms";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-lisungi-bg">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 rounded-full border-2 border-navy border-t-lisungi-yellow animate-spin" />
        <p className="text-sm text-muted2 font-mono">Lisungi AI…</p>
      </div>
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { googleSession } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      if (sid) {
        try { await googleSession(sid); } catch {}
      }
      window.history.replaceState(null, "", "/app");
      navigate("/app", { replace: true });
    })();
  }, [googleSession, navigate]);
  return <LoadingScreen />;
}

function Protected({ admin }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && !["admin", "scientific_admin", "editor", "lab_admin", "director", "super_admin"].includes(user.role))
    return <Navigate to="/app" replace />;
  return <Outlet />;
}

function Root() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/responsable" element={<ResponsibleUse />} />
      <Route path="/f/:publicId" element={<PublicForm />} />
      <Route element={<Protected />}>
        <Route path="/app" element={<AppLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="chat" element={<Chat />} />
          <Route path="chat/:conversationId" element={<Chat />} />
          <Route path="projects" element={<Projects />} />
          <Route path="projects/:projectId" element={<Projects />} />
          <Route path="search" element={<ScienceSearch />} />
          <Route path="documents" element={<Documents />} />
          <Route path="bibliography" element={<Bibliography />} />
          <Route path="similarity" element={<Similarity />} />
          <Route path="notes" element={<Notes />} />
          <Route path="forms" element={<Forms />} />
          <Route path="messages" element={<Messages />} />
          <Route path="billing" element={<Billing />} />
          <Route path="settings" element={<Settings />} />
        </Route>
      </Route>
      <Route element={<Protected admin />}>
        <Route path="/admin" element={<AdminApp />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function FaviconSetter() {
  useEffect(() => {
    api.get("/content").then((r) => {
      const fav = r.data.favicon;
      if (!fav) return;
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
      link.href = fileUrl(fav);
    }).catch(() => {});
  }, []);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <FaviconSetter />
        <Root />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
